// src/pages/AdminDashboard.tsx - Version complète avec gestion des produits
// ✅ MAJ : liste utilisateurs paginée par 10 (plus récents d'abord), filtres admin côté backend
import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import adminService, {
  UserWithAdminInfo,
  AdminStats,
  AdminActivityLog,
  AdminHierarchy,
  SystemSecurity,
  AdminCreate,
  AdminRoleUpdate,
  BulkUserAction
} from '../services/adminService';
import productService, { Product, AdminProductFilters } from '../services/productService';

// Icons (vous pouvez remplacer par vos icônes préférées)
const IconDashboard = () => <span>📊</span>;
const IconUsers = () => <span>👥</span>;
const IconProducts = () => <span>📦</span>;
const IconHierarchy = () => <span>👑</span>;
const IconLogs = () => <span>📋</span>;
const IconSecurity = () => <span>🛡️</span>;
const IconBulk = () => <span>⚡</span>;
const IconTelegram = () => <span>📢</span>;

// ✅ NOUVEAU - Nombre d'utilisateurs affichés par page
const USERS_PAGE_SIZE = 10;

const AdminDashboard = () => {
  const navigate = useNavigate();

  // ===================================
  // STATE MANAGEMENT
  // ===================================

  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentUser, setCurrentUser] = useState<UserWithAdminInfo | null>(null);
  const [users, setUsers] = useState<UserWithAdminInfo[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search and filters for users
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // ✅ NOUVEAU - Pagination utilisateurs
  const [usersPage, setUsersPage] = useState(0);
  const [usersHasMore, setUsersHasMore] = useState(false);

  // Admin hierarchy
  const [adminHierarchy, setAdminHierarchy] = useState<AdminHierarchy | null>(null);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [newAdminData, setNewAdminData] = useState<AdminCreate>({
    name: '',
    email: '',
    password: '',
    phone: '',
    merchant_phone: '',
    is_farmer: false,
    is_customer: true,
    make_super_admin: false
  });

  // Activity logs
  const [activityLogs, setActivityLogs] = useState<AdminActivityLog[]>([]);
  const [logFilters, setLogFilters] = useState({
    admin_id: undefined as number | undefined,
    action_type: '',
    date_from: '',
    date_to: '',
    limit: 50,
    offset: 0
  });

  // Security
  const [systemSecurity, setSystemSecurity] = useState<SystemSecurity | null>(null);

  // Bulk actions for users
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<'activate' | 'deactivate' | 'delete'>('activate');
  const [bulkReason, setBulkReason] = useState('');

  // ✅ ÉTATS POUR LA GESTION DES PRODUITS
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [productFilters, setProductFilters] = useState<AdminProductFilters>({
    search: '',
    category_id: undefined,
    skip: 0,
    limit: 50
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [productStats, setProductStats] = useState<any>(null);

  // ===================================
  // LIFECYCLE & INITIALIZATION
  // ===================================

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const response = await api.get('/auth/me');
        const userData = response.data;

        if (!userData.is_admin) {
          navigate('/');
          return;
        }

        // ✅ ENRICHIR les données utilisateur avec admin_level
        const enrichedUserData = {
          ...userData,
          admin_level: userData.is_super_admin ? 'super_admin' :
                      userData.is_admin ? 'admin' : 'user'
        };

        setCurrentUser(enrichedUserData);
        await fetchStats();

        if (activeTab === 'users') {
          await fetchUsers();
        } else if (activeTab === 'hierarchy') {
          await fetchAdminHierarchy();
        } else if (activeTab === 'logs') {
          await fetchActivityLogs();
        } else if (activeTab === 'security') {
          await fetchSystemSecurity();
        } else if (activeTab === 'products') {
          await fetchProducts();
        }
      } catch (err) {
        console.error('Erreur lors de la vérification du statut d\'administrateur:', err);

        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError('Erreur lors du chargement du tableau de bord. Veuillez réessayer.');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate, activeTab]);

  // ===================================
  // DATA FETCHING FUNCTIONS
  // ===================================

  const fetchStats = async () => {
    try {
      const statsData = await adminService.getAdminStats();
      setStats(statsData);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError('Impossible de charger les statistiques. Veuillez réessayer.');
    }
  };

  // ✅ MODIFIÉ - Pagination côté backend (10 par page, plus récents d'abord).
  // Les options permettent de passer les nouvelles valeurs directement,
  // sans attendre la mise à jour du state React (évite les valeurs périmées).
  const fetchUsers = async (options?: { page?: number; filter?: string; search?: string }) => {
    const page = options?.page ?? usersPage;
    const filter = options?.filter ?? selectedFilter;
    const search = options?.search ?? searchTerm;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('skip', String(page * USERS_PAGE_SIZE));
      // +1 pour savoir s'il existe une page suivante
      params.set('limit', String(USERS_PAGE_SIZE + 1));

      if (search) {
        params.set('search', search);
      }

      switch (filter) {
        case 'farmers':
          params.set('is_farmer', 'true');
          break;
        case 'customers':
          params.set('is_customer', 'true');
          break;
        case 'active':
          params.set('is_active', 'true');
          break;
        case 'inactive':
          params.set('is_active', 'false');
          break;
        case 'super_admin':
          params.set('is_super_admin', 'true');
          break;
        case 'admin':
          params.set('is_admin', 'true');
          params.set('is_super_admin', 'false');
          break;
      }

      const response = await api.get(`/admin/users?${params.toString()}`);
      const rows = response.data;

      setUsersHasMore(rows.length > USERS_PAGE_SIZE);

      // Enrichir les données côté client
      const usersData = rows.slice(0, USERS_PAGE_SIZE).map(user => ({
        ...user,
        admin_level: user.is_super_admin ? 'super_admin' :
                    user.is_admin ? 'admin' : 'user',
        total_actions: user.total_actions || 0,
        last_login: user.last_login || null
      }));

      setUsers(usersData);
      setUsersPage(page);
      setSelectedUsers([]); // éviter d'agir sur des utilisateurs d'une autre page
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      setError('Impossible de charger les utilisateurs. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ MODIFIÉ - Ne charge que les admins (non affecté par la pagination)
  const fetchAdminHierarchy = async () => {
    try {
      const response = await api.get('/admin/users?admins_only=true&limit=50');
      const allUsers = response.data;

      const superAdmins = allUsers.filter(user => user.is_super_admin);
      const regularAdmins = allUsers.filter(user => user.is_admin && !user.is_super_admin);

      setAdminHierarchy({
        super_admins: superAdmins.map(admin => ({
          id: admin.id,
          name: admin.name,
          email: admin.email,
          phone: admin.phone,
          is_super_admin: admin.is_super_admin,
          created_at: admin.created_at,
          recent_actions: 0,
          total_actions: 0
        })),
        regular_admins: regularAdmins.map(admin => ({
          id: admin.id,
          name: admin.name,
          email: admin.email,
          phone: admin.phone,
          is_super_admin: admin.is_super_admin,
          created_at: admin.created_at,
          recent_actions: 0,
          total_actions: 0
        })),
        total_super_admins: superAdmins.length,
        total_regular_admins: regularAdmins.length
      });
    } catch (err) {
      console.error('Erreur lors du chargement de la hiérarchie:', err);
      setError('Impossible de charger la hiérarchie admin.');
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setActivityLogs([]);
    } catch (err) {
      console.error('Erreur lors du chargement des logs:', err);
      setError('API des logs en cours de développement.');
    }
  };

  // ✅ MODIFIÉ - Calcule sur la liste des admins (et non sur la page d'utilisateurs affichée)
  const fetchSystemSecurity = async () => {
    try {
      const response = await api.get('/admin/users?admins_only=true&limit=50');
      const admins = response.data;
      const superAdminsCount = admins.filter(u => u.is_super_admin).length;

      setSystemSecurity({
        security_status: 'healthy',
        super_admin_exists: superAdminsCount > 0,
        total_super_admins: superAdminsCount,
        recent_critical_actions: 0,
        inactive_admin_accounts: admins.filter(u => u.is_admin && !u.is_active).length,
        recommendations: []
      });
    } catch (err) {
      console.error('Erreur lors du chargement de la sécurité:', err);
      setError('Impossible de charger les informations de sécurité.');
    }
  };

  // ✅ FONCTIONS POUR LA GESTION DES PRODUITS
  const fetchProducts = async () => {
    setProductsLoading(true);
    setProductsError('');
    try {
      console.log('📦 Chargement des produits admin...');
      const [productsData, statsData] = await Promise.all([
        productService.getAdminProducts(productFilters),
        productService.getAdminProductsStats()
      ]);

      setProducts(productsData);
      setProductStats(statsData);
      console.log('✅ Produits chargés:', productsData.length);
    } catch (err) {
      console.error('❌ Erreur chargement produits:', err);
      setProductsError('Impossible de charger les produits. Veuillez réessayer.');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await productService.deleteProductAsAdmin(productToDelete.id, deleteReason);

      setShowDeleteModal(false);
      setProductToDelete(null);
      setDeleteReason('');

      await fetchProducts();
      await fetchStats();

      alert('Produit supprimé avec succès');
    } catch (err) {
      console.error('❌ Erreur suppression produit:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la suppression du produit');
    }
  };

  const handleSearchProducts = (e: FormEvent) => {
    e.preventDefault();
    fetchProducts();
  };

  const handleProductFilterChange = (newFilters: Partial<AdminProductFilters>) => {
    setProductFilters({...productFilters, ...newFilters});
    setTimeout(() => {
      fetchProducts();
    }, 0);
  };

  // ===================================
  // TAB MANAGEMENT
  // ===================================

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setError('');

    if (tab === 'users') {
      fetchUsers();
    } else if (tab === 'hierarchy') {
      fetchAdminHierarchy();
    } else if (tab === 'logs') {
      fetchActivityLogs();
    } else if (tab === 'security') {
      fetchSystemSecurity();
    } else if (tab === 'products') {
      fetchProducts();
    }
  };

  // ===================================
  // USER MANAGEMENT ACTIONS
  // ===================================

  const handleDeleteUser = async (userId: number) => {
    if (currentUser?.admin_level !== 'super_admin') {
      alert('Seuls les super admins peuvent supprimer des utilisateurs.');
      return;
    }

    const reason = prompt('Raison de la suppression (optionnel):');
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
      // Si c'était le dernier utilisateur de la page, revenir à la page précédente
      const targetPage = users.length === 1 && usersPage > 0 ? usersPage - 1 : usersPage;
      await fetchUsers({ page: targetPage });
      await fetchStats();
      alert('Utilisateur supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleToggleActive = async (userId: number, currentStatus: boolean) => {
    const action = currentStatus ? 'désactiver' : 'activer';
    const reason = prompt(`Raison pour ${action} cet utilisateur (optionnel):`);

    if (!confirm(`Êtes-vous sûr de vouloir ${action} cet utilisateur ?`)) {
      return;
    }

    try {
      await api.patch(`/admin/users/${userId}/toggle-active${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);
      await fetchUsers();
      alert(`Utilisateur ${action === 'activer' ? 'activé' : 'désactivé'} avec succès`);
    } catch (err) {
      console.error(`Erreur lors de la tentative de ${action}:`, err);
      alert(err.response?.data?.detail || `Erreur lors de la tentative de ${action} l'utilisateur`);
    }
  };

  const handlePromoteAdmin = async (userId: number, currentLevel: string) => {
    if (currentUser?.admin_level !== 'super_admin') {
      alert('Seuls les super admins peuvent modifier les rôles admin.');
      return;
    }

    const reason = prompt('Raison de ce changement de rôle (optionnel):');

    if (!confirm(`Êtes-vous sûr de vouloir effectuer cette action ?`)) {
      return;
    }

    try {
      await api.patch(`/admin/users/${userId}/toggle-admin${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`);

      await fetchUsers();
      await fetchStats();

      alert('Rôle modifié avec succès');
    } catch (err) {
      console.error('Erreur lors de la modification du rôle:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la modification du rôle');
    }
  };

  // ===================================
  // BULK ACTIONS
  // ===================================

  const handleBulkAction = async () => {
    if (selectedUsers.length === 0) {
      alert('Veuillez sélectionner au moins un utilisateur.');
      return;
    }

    const bulkAction: BulkUserAction = {
      user_ids: selectedUsers,
      action: bulkActionType,
      reason: bulkReason || undefined
    };

    try {
      const result = await adminService.bulkUserActions(bulkAction);

      setShowBulkModal(false);
      setSelectedUsers([]);
      setBulkReason('');

      // Après une suppression en masse, la page courante peut être vide : revenir au début
      await fetchUsers(bulkActionType === 'delete' ? { page: 0 } : undefined);
      await fetchStats();

      alert(`Action effectuée sur ${result.successful}/${result.total_processed} utilisateur(s).`);
    } catch (err) {
      console.error('Erreur lors de l\'action en masse:', err);
      alert('Erreur lors de l\'action en masse.');
    }
  };

  // ===================================
  // ADMIN CREATION
  // ===================================

  const handleCreateAdmin = async () => {
    if (!adminService.canPromoteToSuperAdmin(currentUser?.admin_level || '')) {
      alert('Seuls les super admins peuvent créer des admins.');
      return;
    }

    try {
      await adminService.createAdmin(newAdminData);

      setShowCreateAdminModal(false);
      setNewAdminData({
        name: '',
        email: '',
        password: '',
        phone: '',
        merchant_phone: '',
        is_farmer: false,
        is_customer: true,
        make_super_admin: false
      });

      await fetchAdminHierarchy();
      await fetchUsers({ page: 0 }); // le nouvel admin apparaît en tête de liste
      await fetchStats();

      alert('Admin créé avec succès');
    } catch (err) {
      console.error('Erreur lors de la création de l\'admin:', err);
      alert(err.response?.data?.detail || 'Erreur lors de la création de l\'admin');
    }
  };

  // ===================================
  // SEARCH AND FILTERS
  // ===================================

  // ✅ MODIFIÉ - Une nouvelle recherche repart de la page 1
  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    fetchUsers({ page: 0, search: searchTerm });
  };

  // ✅ MODIFIÉ - Passe directement les nouvelles valeurs (plus de setTimeout)
  const handleUserFilterChange = (filter: string) => {
    setSelectedFilter(filter);
    setSearchTerm('');
    fetchUsers({ page: 0, filter, search: '' });
  };

  // ✅ NOUVEAU - Navigation entre les pages
  const goToUsersPage = (page: number) => {
    if (page < 0) return;
    fetchUsers({ page });
  };

  // ===================================
  // RENDER FUNCTIONS
  // ===================================

  const renderDashboard = () => {
    if (!stats) return <div>Chargement des statistiques...</div>;

    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">Tableau de bord administrateur</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-slate font-medium mb-2">Utilisateurs</h3>
            <p className="text-3xl font-bold text-terracotta">{stats.total_users}</p>
            <div className="text-sm text-gray-500 mt-1 space-y-1">
              <p>🌾 {stats.total_farmers} agriculteurs</p>
              <p>🛍️ {stats.total_customers} clients</p>
              <p>⚡ {stats.active_users} actifs</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-slate font-medium mb-2">Administration</h3>
            <p className="text-3xl font-bold text-purple-600">{stats.total_admins}</p>
            <div className="text-sm text-gray-500 mt-1 space-y-1">
              <p>👑 {stats.total_super_admins} super admins</p>
              <p>👥 {stats.total_admins - stats.total_super_admins} admins</p>
              <p>📋 {stats.recent_activities} actions récentes</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-slate font-medium mb-2">Produits</h3>
            <p className="text-3xl font-bold text-terracotta">{stats.total_products}</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-slate font-medium mb-2">Commandes</h3>
            <p className="text-3xl font-bold text-terracotta">{stats.total_orders}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">Activité récente</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.recent_registrations}</p>
              <p className="text-sm text-gray-600">Nouveaux utilisateurs (7 jours)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.recent_activities}</p>
              <p className="text-sm text-gray-600">Actions admin (24h)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.inactive_users}</p>
              <p className="text-sm text-gray-600">Comptes inactifs</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ NOUVEAU - Barre de pagination utilisateurs
  const renderUsersPagination = () => {
    if (users.length === 0 && usersPage === 0) return null;

    const from = usersPage * USERS_PAGE_SIZE + 1;
    const to = usersPage * USERS_PAGE_SIZE + users.length;

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-2">
        <span className="text-sm text-gray-600">
          {users.length > 0
            ? `Utilisateurs ${from} – ${to} · page ${usersPage + 1} (plus récents d'abord)`
            : `Page ${usersPage + 1} vide`}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => goToUsersPage(usersPage - 1)}
            disabled={usersPage === 0 || loading}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Précédent
          </button>
          <button
            onClick={() => goToUsersPage(usersPage + 1)}
            disabled={!usersHasMore || loading}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Suivant →
          </button>
        </div>
      </div>
    );
  };

  const renderUsers = () => {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Gestion des utilisateurs</h2>
            <p className="text-sm text-gray-500 mt-1">
              Derniers inscrits en premier · {USERS_PAGE_SIZE} par page
            </p>
          </div>
          {selectedUsers.length > 0 && (
            <button
              onClick={() => setShowBulkModal(true)}
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition"
            >
              Actions en masse ({selectedUsers.length})
            </button>
          )}
        </div>

        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex space-x-2 flex-wrap">
              {[
                { key: 'all', label: 'Tous' },
                { key: 'super_admin', label: 'Super Admins' },
                { key: 'admin', label: 'Admins' },
                { key: 'farmers', label: 'Agriculteurs' },
                { key: 'customers', label: 'Clients' },
                { key: 'active', label: 'Actifs' },
                { key: 'inactive', label: 'Inactifs' }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => handleUserFilterChange(filter.key)}
                  className={`px-3 py-1 rounded text-sm ${
                    selectedFilter === filter.key
                      ? 'bg-olive text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="flex w-full md:w-auto">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-terracotta w-full"
              />
              <button
                type="submit"
                className="bg-olive text-white px-4 py-2 rounded-r-md hover:bg-terracotta transition"
              >
                🔍
              </button>
            </form>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
            <p className="mt-2">Chargement des utilisateurs...</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
            {users.length === 0 ? (
              <div className="p-6 text-center">
                <p>Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={users.length > 0 && selectedUsers.length === users.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(users.map(u => u.id));
                          } else {
                            setSelectedUsers([]);
                          }
                        }}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Niveau Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rôles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gestion
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className={selectedUsers.includes(user.id) ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, user.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                            }
                          }}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                        {user.created_at && (
                          <div className="text-xs text-gray-400">
                            Inscrit le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                        {user.total_actions > 0 && (
                          <div className="text-xs text-purple-600">{user.total_actions} actions admin</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.email && <div className="text-sm text-gray-900">{user.email}</div>}
                        {user.phone && <div className="text-sm text-gray-500">{user.phone}</div>}
                        {user.merchant_phone && (
                          <div className="text-xs text-green-600">📱 {user.merchant_phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.is_super_admin ? 'bg-red-100 text-red-800' :
                          user.is_admin ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {user.is_super_admin ? 'SUPER ADMIN' :
                           user.is_admin ? 'ADMIN' : 'UTILISATEUR'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {user.is_farmer && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              🌾 Agriculteur
                            </span>
                          )}
                          {user.is_customer && !user.is_admin && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              🛍️ Client
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_active ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            ✅ Actif
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            ❌ Inactif
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => handleToggleActive(user.id, user.is_active)}
                            className={`text-xs ${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                          >
                            {user.is_active ? '⏸️ Désactiver' : '▶️ Activer'}
                          </button>

                          {currentUser?.admin_level === 'super_admin' && (
                            <button
                              onClick={() => handlePromoteAdmin(user.id, user.admin_level)}
                              className="text-xs text-purple-600 hover:text-purple-900"
                            >
                              {user.is_admin ? '⬇️ Retirer Admin' : '👑 Faire Admin'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {currentUser?.admin_level === 'super_admin' && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && renderUsersPagination()}
      </div>
    );
  };

  const renderHierarchy = () => {
    if (!adminHierarchy) return <div>Chargement de la hiérarchie...</div>;

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Hiérarchie Administrative</h2>
          {currentUser?.admin_level === 'super_admin' && (
            <button
              onClick={() => setShowCreateAdminModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition"
            >
              ➕ Créer un Admin
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-800 mb-2">👑 Super Administrateurs</h3>
            <p className="text-3xl font-bold text-red-600">{adminHierarchy.total_super_admins}</p>
            <p className="text-sm text-red-600 mt-1">Accès complet au système</p>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-purple-800 mb-2">👥 Administrateurs</h3>
            <p className="text-3xl font-bold text-purple-600">{adminHierarchy.total_regular_admins}</p>
            <p className="text-sm text-purple-600 mt-1">Gestion utilisateurs et contenu</p>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">👑 Super Administrateurs</h3>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {adminHierarchy.super_admins.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Aucun super administrateur trouvé
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Administrateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membre depuis
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {adminHierarchy.super_admins.map((admin) => (
                    <tr key={admin.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                          <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            SUPER ADMIN
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.email}</div>
                        {admin.phone && <div className="text-sm text-gray-500">{admin.phone}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(admin.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold mb-4">👥 Administrateurs</h3>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {adminHierarchy.regular_admins.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Aucun administrateur trouvé
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-purple-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Administrateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membre depuis
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {adminHierarchy.regular_admins.map((admin) => (
                    <tr key={admin.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                          <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            ADMIN
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.email}</div>
                        {admin.phone && <div className="text-sm text-gray-500">{admin.phone}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(admin.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderLogs = () => {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">📋 Logs d'Activité Administrative</h2>

        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={logFilters.action_type}
                onChange={(e) => setLogFilters({...logFilters, action_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              >
                <option value="">Toutes les actions</option>
                <option value="create_user">Créer utilisateur</option>
                <option value="delete_user">Supprimer utilisateur</option>
                <option value="promote_to_admin">Promouvoir admin</option>
                <option value="demote_from_admin">Rétrograder admin</option>
                <option value="bulk_activate">Activation en masse</option>
                <option value="view_users_list">Consulter utilisateurs</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="datetime-local"
                value={logFilters.date_from}
                onChange={(e) => setLogFilters({...logFilters, date_from: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="datetime-local"
                value={logFilters.date_to}
                onChange={(e) => setLogFilters({...logFilters, date_to: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={fetchActivityLogs}
                className="w-full bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition"
              >
                🔍 Filtrer
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {activityLogs.length === 0 ? (
            <div className="p-6 text-center">
              <p>Aucun log d'activité trouvé</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Heure
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cible
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activityLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {adminService.formatDate(log.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {log.admin_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {adminService.getActionTypeLabel(log.action_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {log.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.target_user_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ip_address || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderSecurity = () => {
    if (!systemSecurity) return <div>Chargement des informations de sécurité...</div>;

    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">🛡️ Sécurité du Système</h2>

        <div className="mb-8 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Statut de Sécurité</h3>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
              systemSecurity.security_status === 'healthy'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {adminService.getSecurityStatusLabel(systemSecurity.security_status)}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{systemSecurity.total_super_admins}</p>
              <p className="text-sm text-gray-600">Super Admins</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{systemSecurity.recent_critical_actions}</p>
              <p className="text-sm text-gray-600">Actions critiques (24h)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{systemSecurity.inactive_admin_accounts}</p>
              <p className="text-sm text-gray-600">Comptes admin inactifs</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${systemSecurity.super_admin_exists ? 'text-green-600' : 'text-red-600'}`}>
                {systemSecurity.super_admin_exists ? '✅' : '❌'}
              </p>
              <p className="text-sm text-gray-600">Super Admin présent</p>
            </div>
          </div>
        </div>

        {systemSecurity.recommendations.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-4">⚠️ Recommandations de Sécurité</h3>
            <ul className="space-y-2">
              {systemSecurity.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-yellow-600 mr-2">•</span>
                  <span className="text-yellow-800">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // ✅ FONCTION POUR GESTION DES PRODUITS
  const renderProducts = () => {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">📦 Gestion des produits</h2>
          <button
            onClick={fetchProducts}
            disabled={productsLoading}
            className="bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition disabled:opacity-50"
          >
            {productsLoading ? '⏳ Chargement...' : '🔄 Actualiser'}
          </button>
        </div>

        {/* Statistiques rapides */}
        {productStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600">Total produits</h3>
              <p className="text-2xl font-bold text-olive">{productStats.total_products}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600">Ruptures de stock</h3>
              <p className="text-2xl font-bold text-red-600">{productStats.products_without_stock}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600">Prix élevés</h3>
              <p className="text-2xl font-bold text-orange-600">{productStats.products_with_high_price}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-medium text-gray-600">Récents (7j)</h3>
              <p className="text-2xl font-bold text-blue-600">{productStats.recent_products}</p>
            </div>
          </div>
        )}

        {/* Filtres et recherche */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <form onSubmit={handleSearchProducts} className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={productFilters.search || ''}
                onChange={(e) => setProductFilters({...productFilters, search: e.target.value})}
                placeholder="Rechercher un produit..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={productFilters.category_id || ''}
                onChange={(e) => handleProductFilterChange({
                  category_id: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
              >
                <option value="">Toutes catégories</option>
                <option value="1">Légumes</option>
                <option value="2">Fruits</option>
                <option value="3">Céréales</option>
              </select>

              <button
                type="submit"
                className="bg-olive text-white px-4 py-2 rounded-md hover:bg-terracotta transition"
              >
                🔍 Rechercher
              </button>
            </div>
          </form>
        </div>

        {/* Messages d'erreur */}
        {productsError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {productsError}
            <button
              className="ml-4 font-medium underline"
              onClick={fetchProducts}
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Liste des produits */}
        {productsLoading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
            <p className="mt-2">Chargement des produits...</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
            {products.length === 0 ? (
              <div className="p-6 text-center">
                <p>Aucun produit trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produit
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stock
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map((product) => {
                      const { needsAttention, reasons } = productService.needsAdminAttention(product);

                      return (
                        <tr key={product.id} className={needsAttention ? 'bg-yellow-50' : ''}>
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <img
                                  className="h-8 w-8 rounded object-cover"
                                  src={productService.getImageUrl(product.thumbnail_url || product.image_url)}
                                  alt={product.name}
                                  onError={(e) => {
                                    e.currentTarget.src = '/placeholder-product.jpg';
                                  }}
                                />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                <div className="text-xs text-gray-500">ID: {product.id}</div>
                                {needsAttention && (
                                  <div className="text-xs text-yellow-600">
                                    ⚠️ {reasons.join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">
                              {productService.formatPrice(product.price)}
                            </div>
                            {product.delivery_fee > 0 && (
                              <div className="text-xs text-blue-600">
                                +{productService.formatDeliveryFee(product.delivery_fee)}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-4">
                            {product.stock === 0 ? (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Rupture
                              </span>
                            ) : product.stock < 5 ? (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Faible ({product.stock})
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                OK ({product.stock})
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => {
                                  window.open(`/products/${product.id}`, '_blank');
                                }}
                                className="bg-blue-100 text-blue-600 hover:bg-blue-200 px-2 py-1 rounded text-xs"
                                title="Voir le produit"
                              >
                                👁️ Voir
                              </button>

                              <button
                                onClick={() => handleDeleteProduct(product)}
                                className={`bg-red-100 text-red-600 hover:bg-red-200 px-2 py-1 rounded text-xs ${
                                  needsAttention ? '' : 'opacity-75'
                                }`}
                                title={needsAttention ? "Supprimer ce produit non conforme" : "Supprimer ce produit"}
                              >
                                🗑️ Supprimer
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {products.length === productFilters.limit && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => handleProductFilterChange({
                skip: (productFilters.skip || 0) + (productFilters.limit || 50)
              })}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition"
            >
              Charger plus de produits
            </button>
          </div>
        )}
      </div>
    );
  };

  // ===================================
  // MODALS
  // ===================================

  const renderCreateAdminModal = () => {
    if (!showCreateAdminModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Créer un Administrateur</h3>
              <button
                onClick={() => setShowCreateAdminModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet *</label>
                <input
                  type="text"
                  value={newAdminData.name}
                  onChange={(e) => setNewAdminData({...newAdminData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={newAdminData.email}
                  onChange={(e) => setNewAdminData({...newAdminData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                <input
                  type="password"
                  value={newAdminData.password}
                  onChange={(e) => setNewAdminData({...newAdminData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={newAdminData.phone}
                  onChange={(e) => setNewAdminData({...newAdminData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                  placeholder="01XXXXXXXX ou +22901XXXXXXXX"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newAdminData.is_farmer}
                    onChange={(e) => setNewAdminData({...newAdminData, is_farmer: e.target.checked})}
                    className="rounded"
                  />
                  <span className="ml-2 text-sm">Agriculteur</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newAdminData.make_super_admin}
                    onChange={(e) => setNewAdminData({...newAdminData, make_super_admin: e.target.checked})}
                    className="rounded"
                  />
                  <span className="ml-2 text-sm">Super Admin</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateAdminModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateAdmin}
                disabled={!newAdminData.name || !newAdminData.email || !newAdminData.password}
                className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition disabled:opacity-50"
              >
                Créer Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBulkModal = () => {
    if (!showBulkModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Action en Masse</h3>
              <button
                onClick={() => setShowBulkModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action à effectuer</label>
                <select
                  value={bulkActionType}
                  onChange={(e) => setBulkActionType(e.target.value as 'activate' | 'deactivate' | 'delete')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                >
                  <option value="activate">Activer les utilisateurs</option>
                  <option value="deactivate">Désactiver les utilisateurs</option>
                  {adminService.canDeleteUser(currentUser?.admin_level || '') && (
                    <option value="delete">Supprimer les utilisateurs</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Raison (optionnel)</label>
                <textarea
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                  placeholder="Expliquez la raison de cette action..."
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Cette action sera appliquée à {selectedUsers.length} utilisateur(s) sélectionné(s).
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowBulkModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkAction}
                className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition"
              >
                Confirmer l'action
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ MODAL DE SUPPRESSION DES PRODUITS
  const renderDeleteModal = () => {
    if (!showDeleteModal || !productToDelete) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-red-600">
                🗑️ Supprimer le produit
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <h4 className="font-medium text-red-800">Produit à supprimer :</h4>
                <p className="text-red-700">{productToDelete.name}</p>
                <p className="text-sm text-red-600">
                  Prix: {productService.formatPrice(productToDelete.price)} |
                  Stock: {productToDelete.stock}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison de la suppression *
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Expliquez pourquoi ce produit ne respecte pas les règles..."
                  required
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteProduct}
                disabled={!deleteReason.trim()}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition disabled:opacity-50"
              >
                Confirmer la suppression
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===================================
  // MAIN RENDER
  // ===================================

  if (loading && !stats && users.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement du tableau de bord administrateur...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button
            className="ml-4 font-medium underline"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Enhanced Sidebar */}
        <div className="lg:w-1/4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate">Administration</h2>
              {currentUser && (
                <div className="mt-2 p-2 bg-gray-50 rounded">
                  <p className="text-sm font-medium">{currentUser.name}</p>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    adminService.getAdminLevelColor(currentUser.admin_level || 'user')
                  }`}>
                    {adminService.getAdminLevelLabel(currentUser.admin_level || 'user')}
                  </span>
                </div>
              )}
            </div>

            <nav className="space-y-2">
              <button
                onClick={() => handleTabChange('dashboard')}
                className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                  activeTab === 'dashboard' ? 'bg-olive text-white' : 'hover:bg-gray-100'
                }`}
              >
                <IconDashboard /> Tableau de bord
              </button>

              <button
                onClick={() => handleTabChange('users')}
                className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                  activeTab === 'users' ? 'bg-olive text-white' : 'hover:bg-gray-100'
                }`}
              >
                <IconUsers /> Utilisateurs
              </button>

              <button
                onClick={() => handleTabChange('products')}
                className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                  activeTab === 'products' ? 'bg-olive text-white' : 'hover:bg-gray-100'
                }`}
              >
                <IconProducts /> Produits
              </button>

              {currentUser?.admin_level === 'super_admin' && (
                <>
                  <button
                    onClick={() => handleTabChange('hierarchy')}
                    className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                      activeTab === 'hierarchy' ? 'bg-olive text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    <IconHierarchy /> Hiérarchie Admin
                  </button>

                  <button
                    onClick={() => handleTabChange('security')}
                    className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                      activeTab === 'security' ? 'bg-olive text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    <IconSecurity /> Sécurité
                  </button>
                </>
              )}

              {(currentUser?.admin_level === 'admin' || currentUser?.admin_level === 'super_admin') && (
                <button
                  onClick={() => handleTabChange('logs')}
                  className={`w-full text-left px-4 py-2 rounded-md flex items-center gap-2 ${
                    activeTab === 'logs' ? 'bg-olive text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  <IconLogs /> Logs d'Activité
                </button>
              )}

              <Link
                to="/admin/broadcast"
                className="block text-left px-4 py-2 rounded-md bg-terracotta text-white hover:bg-olive transition duration-300 flex items-center gap-2"
              >
                <IconTelegram /> Message Telegram
              </Link>
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="lg:w-3/4">
          <div className="bg-white rounded-lg shadow-md p-6">
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'hierarchy' && renderHierarchy()}
            {activeTab === 'logs' && renderLogs()}
            {activeTab === 'security' && renderSecurity()}
            {activeTab === 'products' && renderProducts()}
          </div>
        </div>
      </div>

      {/* Modals */}
      {renderCreateAdminModal()}
      {renderBulkModal()}
      {renderDeleteModal()}
    </div>
  );
};

export default AdminDashboard;